import { useQuery } from '@tanstack/react-query';
import { goalsApi, visionsApi, activitiesApi } from '../../api/client';
import { useTeamStore } from '../../stores/teamStore';
import { LIST_REFETCH_INTERVAL_MS } from '../../constants';

/**
 * Fetches goals, visions, and activities in parallel.
 * Returns raw (encrypted) data — decryption is handled by the screen component.
 */
export function useGoalsList() {
  const activeTeamId = useTeamStore((s) => s.activeTeamId);
  const teamParams = activeTeamId ? { team_id: activeTeamId } : undefined;

  return useQuery({
    queryKey: ['goals', 'list-with-deps', activeTeamId ?? 'personal'],
    refetchOnMount: 'always',
    refetchOnReconnect: true,
    // Cross-device convergence: another device's change shows up here within
    // one refetch interval while the screen is visible, and at once when the
    // app or tab regains focus — regardless of the 2-minute staleTime.
    refetchOnWindowFocus: 'always',
    refetchInterval: LIST_REFETCH_INTERVAL_MS,
    refetchIntervalInBackground: false,
    queryFn: async () => {
      const [goalsRes, visionsRes, activitiesRes] = await Promise.all([
        goalsApi.list(teamParams),
        visionsApi.list(teamParams),
        activitiesApi.list(teamParams),
      ]);
      return {
        goals: goalsRes.data,
        visions: visionsRes.data,
        activities: activitiesRes.data,
      };
    },
  });
}
